<script setup lang="ts">
	import getRatingClassification from '@/helpers/getRatingClassification'
	import { computed, ref } from 'vue'

	import AlertComponent from '@/components/AlertComponent.vue'

	import TripadvisorSubrating from '@/modules/hotels/components/TripadvisorSubrating.vue'
	import { Subrating } from '@/modules/hotels/interfaces/tripadvisorDetails'

	import calculatePercentage from '../../../helpers/calculatePercentage'

	interface Props {
		rating: string
		ratingImageUrl: string
		subratings: Subrating[]
		rankingString: string
		numReviews: string
		reviewRatingCount: { [key: string]: string }
	}

	const props = defineProps<Props>()
	const showingRatingInfo = ref(false)
	const rankingReferenceDate = new Intl.DateTimeFormat('es-MX', {
		month: 'long',
		year: 'numeric',
		timeZone: 'America/Monterrey',
	}).format(new Date())

	const ratingCount = computed(() => {
		const totalCount = Object.values(props.reviewRatingCount).reduce(
			(acc, curr) => acc + +curr,
			0
		)
		return {
			totalCount,
			values: Object.entries(props.reviewRatingCount)
				.map(([key, value]) => ({
					key,
					value,
					percentage:
						totalCount > 0 ? calculatePercentage(+value, totalCount) : '0%',
				}))
				.sort((a, b) => +b.key - +a.key),
		}
	})
</script>

<template>
	<div class="flex flex-col gap-2">
		<div class="flex items-center">
			<p class="text-4xl font-semibold leading-none">{{ rating }}</p>
			<figure class="flex flex-col">
				<p class="px-2">
					{{ getRatingClassification(+rating) }}
					<button
						class="text-gray-700 underline text-xs"
						@click="showingRatingInfo = true"
					>
						({{ numReviews }} reseñas)
					</button>
				</p>
				<div
					v-if="ratingImageUrl"
					class="flex items-center gap-2 bg-white"
				>
					<img
						src="https://files.readme.io/9f59534-Vector_1.png"
						alt="Tripadvisor"
						class="h-6 w-auto max-w-[3rem] object-contain"
					/>
					<img
						:src="ratingImageUrl"
						alt="Calificación de Tripadvisor"
						class="w-full min-w-0 max-w-[10rem]"
					/>
				</div>
			</figure>
		</div>

		<p
			v-if="rankingString"
			class="text-sm text-gray-500"
		>
			{{ rankingString }}. Según viajeros de Tripadvisor,
			{{ rankingReferenceDate }}.
		</p>

		<div class="flex flex-col gap-1 w-full md:max-w-[24rem] md:w-fit">
			<TripadvisorSubrating
				v-for="subrating in subratings"
				:key="subrating.name"
				:subrating="subrating"
			/>
		</div>

		<slot name="awards" />
	</div>

	<AlertComponent
		v-model="showingRatingInfo"
		:dismiss="true"
		type="info"
	>
		<template #title>
			<span class="text-lg font-medium leading-6 text-gray-900"
				>Información sobre la calificación</span
			>
		</template>
		<template #closeButton>Cerrar</template>

		<template #body>
			<div class="flex flex-col gap-1 max-h-72 overflow-y-auto">
				<div class="flex flex-wrap gap-2 gap-y-3">
					<p>Cantidad total: {{ ratingCount.totalCount }}</p>

					<ul class="w-full">
						<li
							v-for="{ key, value, percentage } in ratingCount.values"
							:key="key"
						>
							<div class="flex justify-between gap-2">
								<span class="text-sm font-medium text-gray-900"
									>{{ key }} estrellas</span
								>
								<span class="text-sm text-gray-500">{{ value }} votos</span>
								<span class="text-sm text-gray-500">{{ percentage }}</span>
							</div>
						</li>
					</ul>
				</div>
			</div>
		</template>
	</AlertComponent>
</template>

<style scoped></style>
